import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Permissions } from "../models/permissions";
import {
  CustomerLicenceOptions,
  type CustomerLicence,
} from "../models/session";
import type {
  ZendeskTemp,
  FirebaseAuth,
  AuthAttributes,
  GuestSession,
  AuthedSession,
} from "../models/authState";
import { DEFAULT_ATTRIBUTES, isAuthed } from "../models/authState";
import { authStorage } from "./authStorage";

export { CustomerLicenceOptions, type CustomerLicence };

/**
 * Discriminated union — the store is always in exactly one of these states.
 * No partial updates: every transition sets the complete state.
 */
export type AuthState =
  | { status: "loading" }
  | { status: "guest"; session: GuestSession }
  | { status: "authenticated"; session: AuthedSession }
  | { status: "impersonating"; session: AuthedSession };

export type AuthStoreState = AuthState & {
  isRefreshing: boolean;
  isSigningOut: boolean;
};

export type AuthStoreActions = {
  transition: (state: AuthState) => void;
  setFlags: (
    flags: Partial<Pick<AuthStoreState, "isRefreshing" | "isSigningOut">>,
  ) => void;
  setZendeskTemp: (zendesk: ZendeskTemp) => void;
};

export type AuthStore = AuthStoreState & AuthStoreActions;

const initialState: AuthStoreState = {
  status: "loading",
  isRefreshing: false,
  isSigningOut: false,
};

const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      transition: (state: AuthState) =>
        set(
          (prev) =>
            ({
              ...state,
              session: "session" in state ? state.session : undefined,
              isRefreshing: prev.isRefreshing,
              isSigningOut: prev.isSigningOut,
            }) as Partial<AuthStore>,
        ),

      setFlags: (flags) => set(flags),

      setZendeskTemp: (zendesk: ZendeskTemp) =>
        set((state) => {
          if (!isAuthed(state)) {
            return state;
          }
          return {
            ...state,
            session: {
              ...state.session,
              attributes: {
                ...state.session.attributes,
                zendeskDomainTemp: zendesk.zendeskDomain,
                zendeskKeyTemp: zendesk.zendeskKey,
              },
            },
          };
        }),
    }),
    {
      name: "auth-storage",
      version: 2,
      storage: authStorage,
      partialize: (state): AuthStoreState => {
        // Extract the discriminated union state without actions or transient flags
        const base = { isRefreshing: false, isSigningOut: false };

        switch (state.status) {
          case "authenticated":
          case "impersonating":
            return {
              ...base,
              status: state.status,
              session: {
                ...state.session,
                permissions: Object.fromEntries(
                  Object.entries(state.session.permissions).filter(
                    // migratedAccount permission isn't always sent — if persisted it retains forever
                    ([key]) => key !== "migratedAccount",
                  ),
                ),
              },
            };
          case "guest":
            return { ...base, status: "guest", session: state.session };
          case "loading":
          default:
            return { ...base, status: "loading" };
        }
      },
      migrate: (persisted: unknown, version: number) => {
        if (version === 0 || version === undefined) {
          // v0: old Redux format with loggedIn/impersonating booleans
          const old = persisted as Record<string, unknown> | null;

          if (!old || typeof old !== "object") {
            return { ...initialState };
          }

          if ("loggedIn" in old) {
            const isLoggedIn = !!old.loggedIn;
            const isImpersonating = !!old.impersonating;

            if (isLoggedIn || isImpersonating) {
              const session: AuthedSession = {
                sessionId: (old.sessionId as string) ?? "",
                userId: (old.userId as string) ?? "",
                token: (old.token as string) ?? "",
                expiresAt: (old.expiresAt as number) ?? 0,
                permissions: (old.permissions as Permissions) ?? {},
                firebase: (old.firebase as FirebaseAuth) ?? {
                  url: null,
                  token: null,
                  expires: null,
                  uid: null,
                },
                attributes:
                  (old.attributes as AuthAttributes) ?? DEFAULT_ATTRIBUTES,
                lastRefreshedAt: (old.lastUpdatedAt as number) ?? 0,
              };

              return {
                status: isImpersonating ? "impersonating" : "authenticated",
                session,
                isRefreshing: false,
                isSigningOut: false,
              } as AuthStoreState;
            }

            return { ...initialState };
          }
        }

        return persisted as AuthStoreState;
      },
    },
  ),
);

export { useAuthStore };
